// Every screening report for the driver, newest first.
//
// Not just the latest. Screening is re-run several times a year, so a driver
// accumulates reports, and "cleared then, consider now" is the shape a reviewer is
// actually looking for — the latest result cannot express it at all.
import React, { useState } from 'react';
import { Box, Stack, Typography, CircularProgress } from '../../ui/core';
import { hairline, softFill } from '../../ui/styles';
import { CheckCircleIcon, ChevronDownIcon, OpenInNewIcon } from '../../ui/icons';
import { SectionCard, EmptyRows, Field, timestamp } from './components';

/**
 * How a Checkr result reads.
 *
 * `consider` is a warning rather than an error on purpose: it means a human has to
 * look, not that the driver failed. Treating it as a failure is how someone gets
 * deactivated over a resolved dispute.
 *
 * Colour carries the meaning on the text itself rather than in a filled pill. A row
 * of pills competes with the requirement rows above for attention while saying less,
 * and stacked down a list they read as a wall of badges.
 */
const RESULT = {
  clear: { label: 'Clear', color: 'success.main' },
  consider: { label: 'Consider', color: 'warning.main' },
  suspended: { label: 'Suspended', color: 'error.main' },
  dispute: { label: 'Disputed', color: 'warning.main' },
};

// Every date on a screening report is a real moment — ordered, completed, and each
// webhook — so all of them carry the time. Reports get re-run and webhooks arrive in
// bursts; without seconds two events land on the same stamp and the ordering is lost.

/**
 * One report, with its webhook trail behind a toggle.
 *
 * The trail is collapsed because the report's own result is the answer most of the
 * time; it matters when a result changed and you need to see when and via which
 * webhook.
 */
const Report = ({ report, checkrUrl, isLatest }) => {
  const [open, setOpen] = useState(false);
  const result = RESULT[report.result] || { label: report.result || report.status, color: 'text.primary' };
  const panelId = `screening-events-${report.id}`;

  return (
    <Box sx={{ borderTop: (t) => `1px solid ${hairline(t)}` }}>
      {/* The whole row is the disclosure — a real button, so it is reachable and
          operable by keyboard and announces its state. The previous version hung
          onClick off a Typography, which mouse users could use and nobody else. */}
      <Stack
        component="button"
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        direction="row"
        alignItems="baseline"
        spacing={1.25}
        sx={{
          width: '100%',
          px: 1.75,
          py: 1.2,
          border: 0,
          background: 'none',
          font: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          '&:hover': { backgroundColor: (t) => softFill(t) },
        }}
      >
        {/* Result leads, coloured, at the weight of a heading. Everything else is
            supporting detail at one step down — the hierarchy does the work a row of
            pills was doing. */}
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: result.color, whiteSpace: 'nowrap' }}>
          {result.label}
        </Typography>

        <Typography
          sx={{
            fontSize: '12px',
            color: 'text.secondary',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {report.status}
          {report.manualUpdate && ' · recorded by an admin'}
          {!report.completedAt && ' · not completed'}
          {isLatest && ' · current'}
        </Typography>

        {/* Tabular figures so a column of dates lines up rather than shimmering. */}
        <Typography
          sx={{
            fontSize: '12px',
            color: 'text.disabled',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {timestamp(report.completedAt || report.reportCreatedAt)}
        </Typography>

        {/* Always present. Hiding it when a report had no logged webhooks meant most
            rows showed nothing at all to click — screening_report_log is only
            written by the webhook handler, so older reports have none. The panel
            carries the report's own identifiers either way, which is worth opening
            on its own. */}
        <ChevronDownIcon
          sx={{
            fontSize: 16,
            display: 'block',
            color: 'text.disabled',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </Stack>

      {open && (
        <Box id={panelId} sx={{ backgroundColor: (t) => softFill(t), px: 1.75, py: 1.25 }}>
          {/* The report's own identity, which nothing else on the page shows. The
              report id is what you search Checkr by, so it earns its place even when
              there is no webhook trail. */}
          <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1.25 }}>
            <Field label="Report" value={report.reportId} />
            <Field label="Candidate" value={report.candidateId} />
            <Field label="Ordered" value={timestamp(report.reportCreatedAt)} />
            <Field label="Completed" value={timestamp(report.completedAt)} />
          </Stack>

          {report.events.length === 0 ? (
            // No trail is not the same as nothing happening: the log is only written
            // by the webhook handler, so a report from before that reads as empty.
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
              No webhook events recorded for this report.
            </Typography>
          ) : (
            report.events.map((event, i) => (
            <Stack
              key={`${event.webhookType}-${event.createdAt}-${i}`}
              direction="row"
              alignItems="baseline"
              spacing={1}
              sx={{ py: 0.3 }}
            >
              <Typography sx={{ fontSize: '11.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {event.webhookType}
              </Typography>
              <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', flex: 1, minWidth: 0 }}>
                {[event.status, event.result].filter(Boolean).join(' · ') || '—'}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: 'text.disabled',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {timestamp(event.createdAt)}
              </Typography>
            </Stack>
            ))
          )}

          {/* Outside the row button — an anchor nested in a button is invalid and
              swallows the click. The one accent on this card. */}
          {checkrUrl && (
            <Stack
              component="a"
              href={checkrUrl}
              target="_blank"
              rel="noopener noreferrer"
              direction="row"
              alignItems="center"
              spacing={0.4}
              sx={{ textDecoration: 'none', color: 'accent.main', mt: 0.75 }}
            >
              <Typography sx={{ fontSize: '11.5px', fontWeight: 600 }}>Open at Checkr</Typography>
              <OpenInNewIcon sx={{ fontSize: 13 }} />
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

const ScreeningCard = ({ history, loading, error, backgroundCheck, checkrUrl }) => {
  const reports = history?.reports || [];

  return (
    <SectionCard
      title="Background check"
      icon={<CheckCircleIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        <>
          <Box sx={{ flex: 1 }} />
          {/* Whether it BLOCKS comes from the standing breakdown, not from the
              newest report — an absent or still-running check does not block, and a
              cancelled one does. Deriving it from the list here could disagree with
              what the driver is actually gated on. */}
          {backgroundCheck && (
            <Typography
              sx={{
                fontSize: '11.5px',
                whiteSpace: 'nowrap',
                color: backgroundCheck.passing ? 'text.disabled' : 'error.main',
              }}
            >
              {backgroundCheck.passing ? 'not blocking' : 'blocking'}
            </Typography>
          )}
        </>
      }
    >
      {loading ? (
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={16} />
        </Stack>
      ) : error ? (
        <EmptyRows>{error}</EmptyRows>
      ) : reports.length === 0 ? (
        // Absent is normal, not a gap: a check is triggered when a driver accepts
        // their first trip, so one who has not accepted any has never been screened.
        <EmptyRows>
          Never screened. A check is triggered when the driver accepts their first trip.
        </EmptyRows>
      ) : (
        <>
          {reports.map((report, i) => (
            <Report key={report.id} report={report} checkrUrl={checkrUrl} isLatest={i === 0} />
          ))}
          {history.truncated && (
            <Box sx={{ px: 1.75, py: 1, borderTop: (t) => `1px solid ${hairline(t)}` }}>
              <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                Older reports exist beyond these. Checkr has the full history.
              </Typography>
            </Box>
          )}
        </>
      )}
    </SectionCard>
  );
};

export default ScreeningCard;
